import type * as React from 'react';
import { LazyMotion, domAnimation, m, AnimatePresence } from 'framer-motion';
import { IconCircleArrowUpFilled } from '@tabler/icons-react';
import { CircularLoader } from './SubmitLoadingState';
import { useSuperbarTheme } from './hooks/useSuperbarTheme';

type SubmitButtonProps = {
  isLoading: boolean;
  iconColor?: string;
  size?: number;
  disabled?: boolean;
};

export function SubmitButton({
  isLoading,
  iconColor = '#111827',
  size = 28,
  disabled = false,
}: SubmitButtonProps) {
  const theme = useSuperbarTheme();
  const submitButtonMotion = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    whileHover: { scale: 1.1 },
    whileTap: { scale: 0.95 },
  };

  return (
    <LazyMotion features={domAnimation}>
      <m.button
        type="submit"
        initial={submitButtonMotion.initial}
        animate={submitButtonMotion.animate}
        exit={submitButtonMotion.exit}
        whileHover={submitButtonMotion.whileHover}
        whileTap={submitButtonMotion.whileTap}
        className="caddie-question-submit-button caddietw-border-0 caddietw-appearance-none caddietw-bg-transparent caddietw-cursor-pointer caddietw-p-1"
        aria-busy={isLoading || undefined}
        disabled={disabled || isLoading}
      >
        <span
          className="caddietw-relative caddietw-grid caddietw-place-items-center"
          style={{
            width: size,
            height: size,
          }}
        >
          <AnimatePresence mode="wait" initial={false}>
            {isLoading ? (
              <m.span
                key="loader"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.16, ease: 'easeOut' }}
                className="caddietw-absolute caddietw-inset-0 caddietw-grid caddietw-place-items-center"
              >
                <CircularLoader
                  size={size}
                  durationMs={900}
                  color={theme.components.loader.color}
                  opacity={theme.components.loader.opacity}
                />
              </m.span>
            ) : (
              <m.span
                key="icon"
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.16, ease: 'easeOut' }}
                className="caddietw-absolute caddietw-inset-0 caddietw-grid caddietw-place-items-center"
              >
                <IconCircleArrowUpFilled
                  size={size}
                  style={{ color: iconColor }}
                />
              </m.span>
            )}
          </AnimatePresence>
        </span>
      </m.button>
    </LazyMotion>
  );
}
